import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { io } from 'socket.io-client';

import { api } from '../../../core/network/api';
import { eventKeys } from '../queries/keys';
import { eventPaths } from '../queries/paths';

// TSS vite/03 — server-state + realtime del evento de market (público sin auth).
// PROVISIONAL(0106/Q-320): el backend aún no sirve esto y el payload WS no está
// formalizado. Estrategia ROBUSTA al no-contrato:
//   · el dashboard se sirve por HTTP (GET) con POLLING de base (liveness sin WS);
//   · el socket es un enhancement: hace `join` por public_token y en CUALQUIER
//     evento del server (onAny) refetchea el dashboard — así no dependemos del
//     nombre/shape exacto del broadcast (solo lo usamos como señal "algo cambió").
// Si el socket no conecta, el polling mantiene el estado vivo igual.

const POLL_MS = 5000;

// GET publicLiveState (envuelto en el sobre público). 404 anti-enum = token
// inválido/evento cerrado → lo maneja el layout. `retry:false` para no martillar.
export function useEventDashboard(token) {
    return useQuery({
        queryKey: eventKeys.dashboard(token),
        queryFn: ({ signal }) => api.get(eventPaths.dashboard(token), { signal }).then((r) => r.data),
        enabled: !!token,
        refetchInterval: POLL_MS,
        retry: false,
    });
}

// POST register { name, cedula, email } → { participant_token, … }.
export function useRegisterParticipant(token) {
    return useMutation({
        mutationFn: (body) => api.post(eventPaths.register(token), body).then((r) => r.data),
    });
}

// POST bid { percent } con el participant_token (header X-Participant-Token —
// asunción de transporte; Q-318 dice "header/param participant_token").
export function usePlaceBid(token, participantToken) {
    return useMutation({
        mutationFn: ({ offeringId, percent }) =>
            api.post(
                eventPaths.bid(token, offeringId),
                { percent },
                participantToken ? { headers: { 'X-Participant-Token': participantToken } } : undefined,
            ).then((r) => r.data),
    });
}

// Socket.io del evento: join por public_token → refetch en cada broadcast.
// `onChange` vía ref para no reconectar en cada render. `onEvent(event, args)`
// (opcional) recibe el nombre y el payload crudo de CADA broadcast — lo usa el
// orquestador para extraer la hora de inicio del countdown del propio payload
// del WS (por si no viaja en el GET del dashboard). Devuelve el booleano de
// conexión (reactivo) para el indicador "En vivo".
export function useEventSocket(token, onChange, onEvent) {
    const onChangeRef = useRef(onChange);
    const onEventRef = useRef(onEvent);
    useEffect(() => { onChangeRef.current = onChange; onEventRef.current = onEvent; });
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        if (!token) return undefined;
        // Mismo origen que la API (proxy /socket.io en dev). polling PRIMERO, luego
        // upgrade a websocket si el server lo ofrece (el handshake trae upgrades:[]
        // → forzar ws primero solo genera "WebSocket is closed before the
        // connection is established" y cae a polling igual). El token NO va en el
        // handshake: el contrato pide join_event tras connect.
        const socket = io({ path: '/socket.io', transports: ['polling', 'websocket'], reconnection: true });

        socket.on('connect', () => {
            setConnected(true);
            // Contrato market: tras connect, emit('join_event', { public_token }, ack).
            // ack.ok=true → ack.state es el snapshot (mismo shape que
            // market_event:state) → lo enrutamos como un state para sembrar
            // offerings + countdown de una. ack.ok=false → token inválido (anti-enum).
            socket.emit('join_event', { public_token: token }, (ackRes) => {
                if (import.meta.env.DEV) console.debug('[evento][ws] join_event ack', ackRes);
                if (ackRes?.ok && ackRes.state) {
                    onEventRef.current?.('market_event:state', [ackRes.state]);
                } else if (ackRes && ackRes.ok === false && import.meta.env.DEV) {
                    console.warn('[evento][ws] join rechazado:', ackRes.error);
                }
            });
        });
        socket.on('disconnect', () => setConnected(false));
        // Broadcasts del contrato: market_event:state (snapshot completo) y
        // market_event:countdown (bloque countdown). Se pasan crudos a onEvent y se
        // avisa a onChange (refetch de respaldo, p.ej. la pantalla de proyección).
        socket.onAny((event, ...args) => {
            if (event === 'connect' || event === 'disconnect' || event === 'connect_error') return;
            if (import.meta.env.DEV) console.debug('[evento][ws]', event, ...args);
            onEventRef.current?.(event, args);
            onChangeRef.current?.();
        });

        return () => { socket.disconnect(); setConnected(false); };
    }, [token]);

    return connected;
}
